import * as THREE from "three";

import LoaderManager from './managers/LoaderManager'

import fragment from "./shader/fragment.glsl";
import vertex from "./shader/vertex.glsl";

// post
import { EffectComposer } from "/node_modules/three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "/node_modules/three/examples/jsm/postprocessing/RenderPass.js";
import { SMAAPass } from "three/addons/postprocessing/SMAAPass.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";

// menu transition
import transitionFragment from "./shader/transition/fragment.glsl";
import transitionVertex from "./shader/transition/vertex.glsl";

import { Howl } from "howler";

import gsap from "gsap";

export default class Sketch {
	constructor(options) {
		if (options.dom === null) return;

		this.init(options);
	}

	init = async (options) => {
		console.log('assets loading...');
        const assets = [
            {
                name:'gem_model',
                gltf:__webpack_public_path__ + "../public/models/gems.glb"
            },
            {
                name:'gem_texture',
                texture:__webpack_public_path__ + "../public/img/texture-building.jpg"
            },
            {
                name:'triangle',
                gltf:__webpack_public_path__ + "../public/models/triangle.glb",
            },
        ];

        await LoaderManager.load(assets);
		console.log('assets loaded');
		
		if ( document.querySelector('.sezam')) {
			gsap.set(".sezam svg", { display: 'none' });
			gsap.to(".sezam .button-enter", { y: "0", opacity: 1, delay:1 });
		}

		this.sound = new Howl({
			src: [__webpack_public_path__ + "../public/audios/scene.mp3"],
			loop: true,
			volume: 0.1,
		});


		this.pageScene = new THREE.Scene();
		this.menuScene = new THREE.Scene();
		this.finalScene = new THREE.Scene();

		this.originalWidth = 1728;
		this.originalHeight = 900;

		this.isLockedBySezam = options.isLockedBySezam;
		this.isHome = options.isHome;
		this.container = options.dom;
		this.vp = {
			width: window.innerWidth,
			height: window.innerHeight,
			dpr: Math.min(devicePixelRatio, 2 || 1),
		};
		this.width = this.container.offsetWidth;
		this.height = this.container.offsetHeight;
		this.renderer = new THREE.WebGLRenderer({
			antialias: true,
			alpha: true,
		});
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
		this.renderer.setSize(this.width, this.height);
		this.container.appendChild(this.renderer.domElement);

		// cameras
		this.camera = new THREE.PerspectiveCamera(
			70,
			this.width / this.height,
			0.1,
			100
		);
		this.camera.position.set(0, 0, 6);

		this.cameraMenu = new THREE.PerspectiveCamera(
			70,
			this.width / this.height,
			0.1,
			100
		);
		this.cameraMenu.position.set(0, 0, 6);

		let frustumSize = 1;
		this.finalCamera = new THREE.OrthographicCamera(
			frustumSize / -2,
			frustumSize / 2,
			frustumSize / 2,
			frustumSize / -2,
			-1000,
			1000
		);

		this.time = 0;
		this.clock = new THREE.Clock();

		this.isPlaying = true;
		this.isMenuPlaying = false;
		this.isPagePlaying = true;
		this.initCursor();
		this.addObjects();
		this.addParticles();
		this.initTransition();
		this.resize();
		this.postProcessing();
		this.render();
		this.setupResize();

		let that = this;
		if (!this.isLockedBySezam)
			setTimeout(function () {
				that.enter();
			}, 500);
	}

	initCursor() {
		this.cursor = {};
		this.cursor.x = 0;
		this.cursor.y = 0;
		let that = this;
		window.addEventListener("mousemove", (event) => {
			that.cursor.x = event.clientX / that.width - 0.5;
			that.cursor.y = event.clientY / that.height - 0.5;
		});
	}

	addObjects() {

		const gltf = LoaderManager.assets['gem_model'].gltf;
		const t = LoaderManager.assets['gem_texture'].texture;
		t.wrapS = t.wrapT = THREE.MirroredRepeatWrapping;
		const material = new THREE.ShaderMaterial({
			side: THREE.DoubleSide,
			uniforms: {
				time: { type: "f", value: 0 },
				building: { value: t },
				resolution: { type: "v4", value: new THREE.Vector4() },
				uvRate1: {
					value: new THREE.Vector2(1, 1),
				},
			},
			fragmentShader: fragment,
			vertexShader: vertex,
		});

		this.gems = new THREE.Group();
		this.pageScene.add(this.gems);

		this.gem = gltf.scene.children[0];
		this.gems.add(this.gem);

		if (document.querySelector(".page-thumbnail")) {
			const pageThumbnailUrl =
				document.querySelector(".page-thumbnail").dataset.src;
			const pageThumbnailTexture = new THREE.TextureLoader().load(
				pageThumbnailUrl
			);
			pageThumbnailTexture.wrapS = pageThumbnailTexture.wrapT =
				THREE.RepeatWrapping;
			pageThumbnailTexture.repeat.y = -1;
			this.gem.children[0].material = this.gem.children[0].material.clone();
			this.gem.children[0].material.map = pageThumbnailTexture;
		}
		this.gemLogo = gltf.scene.children[0];
		this.gems.add(this.gemLogo);

		this.gemNotaire = gltf.scene.children[0];
		this.gems.add(this.gemNotaire);

		this.menuGems = this.gems.clone(true);
		this.menuScene.add(this.menuGems);
		




		const gltfTriangle = LoaderManager.assets['triangle'].gltf;
		const triangle = gltfTriangle.scene.children[0];
		this.triangles = new THREE.Group();
		this.triangles.add(triangle);
		triangle.position.x = 8 + 2 * Math.random();
		triangle.rotation.x = Math.PI * Math.random();
		triangle.rotation.y = Math.PI * Math.random();
		triangle.rotation.z = Math.PI * Math.random();

		const triangle2 = triangle.clone();
		triangle2.position.x = -8 - 2 * Math.random();
		triangle2.rotation.x = Math.PI * Math.random();
		triangle2.rotation.y = Math.PI * Math.random();
		triangle2.rotation.z = Math.PI * Math.random();
		this.triangles.add(triangle2);

		const triangle3 = triangle.clone();
		triangle3.position.x = -7 - 2 * Math.random();
		triangle3.position.y = 2;
		triangle3.rotation.x = Math.PI * Math.random();
		triangle3.rotation.y = Math.PI * Math.random();
		triangle3.rotation.z = Math.PI * Math.random();
		this.triangles.add(triangle3);

		const triangle4 = triangle.clone();
		triangle4.position.x = 7 + 2 * Math.random();
		triangle4.position.y = 3;
		triangle4.rotation.x = Math.PI * Math.random();
		triangle4.rotation.y = Math.PI * Math.random();
		triangle4.rotation.z = Math.PI * Math.random();
		this.triangles.add(triangle4);

		this.triangles.scale.set(0, 0, 0);
		this.pageScene.add(this.triangles);
		


		// LIGHTS 
		this.pointLight = new THREE.PointLight(0xffffff, 1, 50);
		this.pointLight.position.x = 0;
		this.pointLight.position.y = 3;
		this.pointLight.position.z = 4;
		this.pageScene.add(this.pointLight);

		const light = new THREE.AmbientLight(0x666666, 2); // soft white light
		this.pageScene.add(light);

		/* menu Scene */
		this.pointLight2 = new THREE.PointLight(0x209fba, 1.5, 30);
		this.pointLight2.position.x = 1;
		this.pointLight2.position.y = 1;
		this.pointLight2.position.z = 4;
		this.menuScene.add(this.pointLight2);

		const light2 = new THREE.AmbientLight(0x666666, 0.5); // soft white light
		this.menuScene.add(light2);

		const backgroundGeometry = new THREE.PlaneGeometry(10, 10);
		const backgroundMaterial = new THREE.MeshStandardMaterial({
			color: 0x242976,
		});
		this.background = new THREE.Mesh(backgroundGeometry, backgroundMaterial);
		this.background.position.y = 0;
		this.background.position.z = -2;
		this.background.scale.set(10, 10, 10);
		this.menuScene.add(this.background);
	}

	explode() {
		this.explosionTimeline = gsap
		.timeline({ paused:true, defaults: { duration: 0.3, ease: "Power3.easeOut" } })
		.to(this.triangles.scale, {
			x: 1,
			y: 1,
			z: 1,
		});

		if (!this.explosed) {
			//console.log(window.innerHeight / 600)
			this.explosed = 1;
			this.triangles.originalY = this.camera.position.y - 6 * window.innerHeight / 600;
			//console.log(this.camera.position.y - 6 * window.innerHeight / 600)
			this.triangles.scrollY = window.scrollY;
			gsap.set(this.triangles.position, { y: this.triangles.originalY });
			this.explosionTimeline.play();
		}
	}

	implode() {
		if (this.explosed) {
			this.explosed = 0;
			this.explosionTimeline.reverse();
		}
	}

	moveTriangles(progress) {
		// explosed
		if (this.explosed) {
			this.triangles.position.y = this.triangles.originalY + 15 * progress;
		}
	}

	initTransition() {
		this.settings = {
			progress: 0,
		};
		// const gui = new dat.GUI()
		// const transitionFolder = gui.addFolder('progress')
		// transitionFolder.add(this.settings, 'progress', 0, 1.0, 0.01).name('progress')
		// transitionFolder.open();

		this.texturePage = new THREE.WebGLRenderTarget(this.width, this.height);
		this.texturePage.texture.colorSpace = THREE.SRGBColorSpace;

		this.textureMenu = new THREE.WebGLRenderTarget(this.width, this.height);
		this.textureMenu.texture.colorSpace = THREE.SRGBColorSpace;

		this.finalScene = new THREE.Scene();
		let geometry = new THREE.PlaneGeometry(1, 1);
		this.transitionMaterial = new THREE.ShaderMaterial({
			extensions: {
				derivatives: "#extension GL_OES_standard_derivatives : enable",
			},
			side: THREE.DoubleSide,
			uniforms: {
				time: { value: 0 },
				progress: { value: 0 },
				menuScene: { value: null },
				pageScene: { value: null },
			},
			vertexShader: transitionVertex,
			fragmentShader: transitionFragment,
		});
		let mesh = new THREE.Mesh(geometry, this.transitionMaterial);
		this.finalScene.add(mesh);
	}

	addParticles() {
		let particlesCount = 15;
		let color, size, sprite, materialsParticle;
		const textureLoader = new THREE.TextureLoader();
		let spriteOne = textureLoader.load(
				__webpack_public_path__ +
					"../public/textures/particles-b/particles-1.png"
			),
			spriteTwo = textureLoader.load(
				__webpack_public_path__ +
					"../public/textures/particles-b/particles-2.png"
			),
			spriteThree = textureLoader.load(
				__webpack_public_path__ +
					"../public/textures/particles-b/particles-3.png"
			),
			spriteFour = textureLoader.load(
				__webpack_public_path__ +
					"../public/textures/particles-b/particles-4.png"
			),
			spriteFive = textureLoader.load(
				__webpack_public_path__ +
					"../public/textures/particles-b/particles-5.png"
			);

		var dotGeometry = new THREE.BufferGeometry();
		const vertices = [];

		for (let i = 0; i < particlesCount; i++) {
			const x = 24 * Math.random() - 12;
			const y = 24 * Math.random() - 12;
			const z = 0;
			vertices.push(x, y, z);
		}

		dotGeometry.setAttribute(
			"position",
			new THREE.Float32BufferAttribute(vertices, 3)
		);
		let parameters = [
			[[1, 1, 1], 0.02, spriteOne],
			[[0.95, 1, 1], 0.015, spriteTwo],
			[[0.9, 1, 1], 0.01, spriteThree],
			[[0.85, 1, 1], 0.02, spriteFour],
			[[0.8, 1, 1], 0.01, spriteFive],
		];
		for (let i = 0; i < parameters.length; i++) {
			(color = parameters[i][0]),
				(size = parameters[i][1]),
				(sprite = parameters[i][2]);
			materialsParticle = new THREE.PointsMaterial({
				color: color,
				size: size,
				map: sprite,
				alphaTest: 0.01,
				depthTest: false,
				blending: THREE.AdditiveBlending,
				transparent: true,
			});
			const particles = new THREE.Points(dotGeometry, materialsParticle);
			particles.rotation.x = 4 * Math.PI * Math.random() - Math.PI * 2;
			particles.rotation.y = 4 * Math.PI * Math.random() - Math.PI * 2;
			particles.rotation.z = 4 * Math.PI * Math.random() - Math.PI * 2;
			(particles.name = "particle"), particles.scale.set(0.5, 0.5, 0.5);
			this.menuScene.add(particles);
		}
	}

	postProcessing() {
		this.postProcessed = true;

		// POST PROCESSING
		this.composer = new EffectComposer(this.renderer);
		this.composer.setSize(this.width, this.height);
		const renderScene = new RenderPass(this.pageScene, this.camera);
		this.composer.addPass(renderScene);

		const pixelRatio = this.renderer.getPixelRatio();
		const pass = new SMAAPass(
			window.innerWidth * pixelRatio,
			window.innerHeight * pixelRatio
		);
		this.composer.addPass(pass);

		const outputPass = new OutputPass();
		this.composer.addPass(outputPass);
	}

	setupResize() {
		window.addEventListener("resize", this.resize.bind(this));
	}

	resize() {
		this.width = window.innerWidth;
		this.height = window.innerHeight;
		this.renderer.setSize(this.width, this.height);
		this.camera.aspect = this.width / this.height;
		this.camera.updateProjectionMatrix();
	}

	enter() {

		this.entered = true;
		const enterTl = gsap
			.timeline({ defaults: { duration: 3, ease: "Power3.easeOut" } })
			.to(this.gems.rotation, { y: Math.PI * 2 });

		if (window.innerWidth < 767) {
			enterTl.to(this.gemLogo.position, { x: -0.8, y: 2, z: 0 }, "<");
		} else if (window.innerWidth < 1200) {
			enterTl.to(this.gemLogo.position, { x: -2, y: 2, z: 0 }, "<");
		} else {
			enterTl.to(this.gemLogo.position, { x: -5, y: 2, z: 0 }, "<");
		}
		enterTl.to(this.gemLogo.rotation, { y: Math.PI * 3 - 0.4 }, "<");

		if (window.innerWidth < 767) {
			enterTl.to(
				this.gemNotaire.position,
				{ x: 0, y: 3.3, z: -0.8, duration: 3.3 },
				"<"
			);
		} else if (window.innerWidth < 1200) {
			enterTl.to(
				this.gemNotaire.position,
				{ x: -1.2, y: 3.6, z: -0.8, duration: 3.3 },
				"<"
			);
		} else {
			enterTl.to(
				this.gemNotaire.position,
				{ x: -4.2, y: 3, z: -0.8, duration: 3.3 },
				"<"
			);
		}
		enterTl
			.to(this.gemNotaire.scale, { x: 0.7, y: 0.7, z: 0.7, duration: 3.3 }, "<")
			.to(
				this.gemNotaire.rotation,
				{ y: Math.PI * 3 - 0.4, duration: 3.3 },
				"<"
			);

		if (document.querySelector("#page-article")) {
			gsap.set(this.gem.scale, { x: 0, y: 0, z: 0 });
		} else {
			if (window.innerWidth < 767) {
				enterTl
					.to(this.gem.position, { x: 1, y: 1.4, z: 1 }, "<")
					.to(this.gem.rotation, { y: Math.PI * 2 - 0.4 }, "<")
					.to(this.gem.scale, { x: 0.9, y: 0.9, z: 0.9 }, "<");
			} else if (window.innerWidth < 1200) {
				enterTl
					.to(this.gem.position, { x: 1.5, y: 0.7, z: 1 }, "<")
					.to(this.gem.rotation, { y: Math.PI * 2 - 0.4 }, "<")
					.to(this.gem.scale, { x: 1.2, y: 1.2, z: 1.2 }, "<");
			} else {
				enterTl
					.to(this.gem.position, { x: 2, y: -0.2, z: 1 }, "<")
					.to(this.gem.rotation, { y: Math.PI * 2 - 0.4 }, "<")
					.to(this.gem.scale, { x: 2, y: 2, z: 2 }, "<");
			}
		}

		this.sound.play();
	}

	openMenuScene() {
		this.isMenuPlaying = true;
		let that = this;
		gsap.to(this.settings, {
			duration: 1,
			progress: 1,
			onComplete: function () {
				that.isPagePlaying = false;
			},
		});
	}

	closeMenuScene() {
		this.isPagePlaying = true;
		let that = this;
		gsap.to(this.settings, {
			duration: 1,
			progress: 0,
			onComplete: function () {
				that.isMenuPlaying = false;
			},
		});
	}

	render() {
		if (!this.isPlaying) return;

		const parallaxX = this.cursor.x;

		const deltaTime = this.clock.getDelta();
		this.camera.position.x +=
			(parallaxX - this.camera.position.x) * 2 * deltaTime;

		// Update objects
		const elapsedTime = this.clock.getElapsedTime();
		this.time += 0.05;

		// pageScene
		if (this.isPagePlaying) {
			if (typeof this.gem !== "undefined" && !this.entered)
				this.gems.rotation.y = 0.5 * elapsedTime;
			if (typeof this.gem !== "undefined" && this.entered) {
				this.gem.rotation.y =
					Math.PI * 2 - 0.4 - 0.1 * Math.sin(this.clock.elapsedTime);
				this.gemLogo.rotation.y =
					Math.PI * 3 - 0.4 - 0.1 * Math.sin(this.clock.elapsedTime);
			}

			this.renderer.setRenderTarget(this.texturePage);
			this.renderer.render(this.pageScene, this.camera);
			this.transitionMaterial.uniforms.pageScene.value =
				this.texturePage.texture;
		}

		// menuScene
		if (this.isMenuPlaying) {
			//particles
			var e = (6e-5 * Date.now()) / 10;
			for (var i = 0; i < this.menuScene.children.length; i++) {
				var a = this.menuScene.children[i];
				"particle" == a.name && (a.rotation.y = e * (i < 4 ? i + 1 : -(i + 1)));
			}
			if (typeof this.menuGems !== "undefined")
				this.menuGems.rotation.y = 0.5 * elapsedTime;

			this.renderer.setRenderTarget(this.textureMenu);
			this.renderer.render(this.menuScene, this.cameraMenu);
			this.transitionMaterial.uniforms.menuScene.value =
				this.textureMenu.texture;
		}

		this.transitionMaterial.uniforms.progress.value = this.settings.progress;

		this.renderer.setRenderTarget(null);
		this.renderer.render(this.finalScene, this.finalCamera);

		if (this.postProcessed && !this.isMenuPlaying) {
			this.composer.render();
		} else {
			this.renderer.setRenderTarget(null);
			this.renderer.render(this.finalScene, this.finalCamera);
		}
		requestAnimationFrame(this.render.bind(this));
	}

	setCameraPosition(y) {
		this.camera.position.y = y;
		this.pointLight.position.y = y + 1;
	}
}
